<template>
  <div>
    <div class="display-images-wrapper">

      <div class="brand-info">
        <h2>{{displayName}}</h2>
          <div v-if="brandSelected" class="button-wrapper">
            <div @click="clickedSponsorRequests(brandDisplayId)"
                class="btn-wrapper"
                :class="{disabledButton: sponsorUpdated }">
              <fai :icon="['fas', 'comment-dots']" class="mr-1" />
              <span>{{ sponsorUpdated ? 'sponsorship requested' : 'sponsor my campaign' }}</span>
            </div>

            <div @click="clickedInventoryRequests(brandDisplayId)"
              class="btn-wrapper mt-3.5"
              :class="{disabledButton: supplyUpdated }">
                <fai :icon="['fal', 'box-open']" class="mr-1" />
                <span>{{ supplyUpdated ? 'supply requested' : 'low supply? notify the brand' }}</span>
            </div>
            <p class="error" v-if="errorMessage">{{errorMessage}}</p>
          </div>
      </div>

      <div>
        <sb-loading-icon v-if="isLoadingImage"/>
        <div v-show="isImageLoaded" class="images-container">
          <section v-if="hasSecondaryImages">
            <div :class="{ activated: dynamicMainBrandImgId === activeImageId }"
              class="side-images"
              @click="activeImageId = dynamicMainBrandImgId">
              <img :src="demoImg" alt="side-img" @click="clickedSideImg(demoImg)">
            </div>
            <div class="side-images"
              :key="index"
              v-for="(image, index) in secondaryImages"
              :class="{ activated: image.id === activeImageId }"
              @click="activeImageId = image.id">
              <img @click="clickedSideImg(image.url)" :src="image.url" alt="side-img">
            </div>
          </section>
          <div class="main-image">
            <img class="main-img" :src="dynamicMainBrandImg" alt="main-img" :class="{ round: !hasSecondaryImages }" @load="imageLoaded">
          </div>
        </div>
      </div>
    </div>

    <modal
      v-if="showModal && isMobile && loaded"
      wrapperClasses="brand-info-modal"
      :showClose="true"
      @close="closeModal">
      <div slot="body" class="body">
        <h2>{{displayName}}</h2>
        <carousel :slides="mobileSlides.slides" />
        <p v-if="supplyUpdated" class="submitted">inventory request submitted</p>
        <button v-else @click="clickedInventoryRequests(brandDisplayId)" class="button inventory">low on supply? click here to notify</button>
        <p v-if="sponsorUpdated" class="submitted">sponsored request submitted</p>
        <button v-else @click="clickedSponsorRequests(brandDisplayId)" class="button sponsor">sponsor my campaign</button>
        <p v-if="errorMessage" class="error">{{errorMessage}}</p>
        <a target="_blank" href="http://calendly.com/mkilgo" class="button book-demo">need more info?</a>
      </div>
    </modal>
  </div>
</template>

<script>
 import { brandsMarketplaceAPI } from "../../services/api/brandsMarketplace"
 import { parseISO, isToday } from 'date-fns'
 import { Carousel } from '@springbig/sb-components'
 import Modal from '../shared/Modal'

  export default {
    props: {
      marketplaceDisplay: Object,
      isMobile: Boolean,
      showModal: Boolean
    },

    components: {
      Modal,
      Carousel
    },

    name: 'BrandsMarketplaceDisplay',

    data() {
      return {
        dynamicMainBrandImg: null,
        dynamicMainBrandImgId: null,
        displayName: null,
        demoImg: null,
        secondaryImages: null,
        activeImageId: null,
        supplyUpdated: false,
        sponsorUpdated: false,
        brandsLoading: false,
        brandDisplayId: null,
        brandSelected: null,
        errorMessage: null,
        merchantInventoryDate: null,
        merchantSponsorDate: null,
        mobileSlides: {},
        loaded: false,
        isLoadingImage: true,
        isImageLoaded: false
      }
    },

    computed: {
      hasSecondaryImages() {
        return !!this.secondaryImages?.length
      }
    },

    methods: {
      imageLoaded() {
        this.isLoadingImage = false
        this.isImageLoaded = true
      },

      configureDisplay(display) {
        if (display.hasOwnProperty('last_merchant_sponsor_request_at')) {
          this.brandSelected = true
        } else {
          this.brandSelected = false
        }

        this.demoImg = display.primary_image.url
        this.displayName = display.name
        this.dynamicMainBrandImg = display.primary_image.url
        this.secondaryImages = display.secondary_images
        this.dynamicMainBrandImgId = display.primary_image.id
        this.activeImageId = display.primary_image.id
        this.brandDisplayId = display.id
        this.merchantInventoryDate = display.last_merchant_inventory_request_at
        this.merchantSponsorDate = display.last_merchant_sponsor_request_at

        let inventoryResult = isToday(parseISO(this.merchantInventoryDate))
        let sponsorResult = isToday(parseISO(this.merchantSponsorDate))

        if (inventoryResult) {
          this.supplyUpdated = true
        } else {
          this.supplyUpdated = false
        }

        if (sponsorResult) {
          this.sponsorUpdated = true
        } else {
          this.sponsorUpdated = false
        }

        if (this.isMobile && this.showModal) {
          this.createSlidesArray('mobileSlides', display.secondary_images)
        }

        this.loaded = true
      },

      createSlidesArray(dataProp, arr) {
        let carousel = {
          slides: []
        }

        arr.forEach((element) => {
          carousel.slides.push({
            label: null,
            imgSrc: element.url,
            id: element.id,
            createdAt: null
          })
        })

        carousel.slides.unshift({
          label: null,
          imgSrc: this.dynamicMainBrandImg,
          id: this.dynamicMainBrandImgId,
          createdAt: null
        })

        this[dataProp] = carousel
      },

      closeModal() {
        this.$emit('closeModal')
        this.loaded = false
      },

      clickedSideImg(url) {
        this.dynamicMainBrandImg = url
      },

      async clickedSponsorRequests(id) {
        this.brandsLoading = true
        try {
          const marketActionRequest = await brandsMarketplaceAPI.postMarketplaceSponsor({brand_display_id: id})
          this.sponsorUpdated = true
        } catch (error) {
          this.errorMessage = error
        }
        this.brandsLoading = false
      },

      async clickedInventoryRequests(id) {
        this.brandsLoading = true
        try {
          const marketActionRequest = await brandsMarketplaceAPI.postMarketplaceInventory({brand_display_id: id})
          this.supplyUpdated = true
        } catch (error) {
          this.errorMessage = error
        }
        this.brandsLoading = false
      },
    },

    created() {
      !!this.marketplaceDisplay && this.configureDisplay(this.marketplaceDisplay)
    },

    watch: {
      marketplaceDisplay: function () {
        !!this.marketplaceDisplay && this.configureDisplay(this.marketplaceDisplay)
      }
    }
  }
</script>

<style scoped>
  .images-container .activated {
    border: 1px solid #128ff9;
    border-top-left-radius: 15px;
    border-bottom-left-radius: 15px;
  }

  .disabledButton {
    opacity: 0.50;
    cursor: not-allowed !important;
    pointer-events: none;
  }

  .button-wrapper {
    display: flex;
    flex-direction: column;
  }

  .mr-1 {
    color: white;
    margin-right: 17px;
    font-size: 50px;
  }

  .btn-wrapper {
    background-color: #128ff9;
    width: 200px;
    text-align: center;
    border-radius: 10px;
    display: flex;
    padding: 10px;
    font-weight: bold;
    color: white;
    cursor: pointer;
  }

  .images-container .main-img {
    width: 500px;
    height: 280px;
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
    border: 2px solid #128ff9;
  }

  .images-container .main-img.round {
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
  }

  .images-container .side-images {
    cursor: pointer;
  }

  .images-container .main-img {
    position: relative;
    z-index: -1;
  }

  /* important needed  to override body .button  */
  .brand-info .inventory {
    margin-top: 25px !important;
  }

  .brand-info .sponsor,
  .brand-info .inventory,
  .brand-info .refer-brand,
  .brand-info .book-demo {
    width: 255px;
    font-weight: 400;
    font-family: 'AileronThin';
  }

  .book-demo {
    margin-top: 10px;
  }

  .display-images-wrapper .submitted {
    color: limegreen;
    font-weight: 600;
    margin-top: 10px;
    font-size: 18px;
    width: auto;
  }

  .display-images-wrapper p.submitted:first-child {
    margin-top: 20px;
  }

  .brand-info .error {
    color: red;
    font-weight: 600;
  }

  .refer-brand {
    margin-top: 11px;
  }

  .sub-title {
    position: relative;
    bottom: 35px;
    text-align: center;
    font-family: 'AileronThin';
  }

  section.refer-brand-wrapper label {
    font-size: 19px;
  }

  .desktop {
    display: block;
  }

  .mobile {
    display: none;
  }

  @media only screen and (max-width: 900px) {
    .desktop {
      display: none;
    }

    .mobile {
      display: inline-block;
      font-weight: bolder;
      cursor: pointer;
      color: #128ff9;
      font-size: 20px;
      position: relative;
      top: 35px;
      left: -6px;
    }

    .sub-title {
      font-size: 1.2rem;
    }

    .error-banner {
      color: red;
    }
  }
</style>
